/**
 * Faxes Page
 * View and manage incoming and outgoing faxes
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Printer,
  Send,
  Download,
  Inbox,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeft,
  Search,
  RefreshCw,
  Eye,
  MoreHorizontal,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  AlertCircle,
  ExternalLink,
  Settings,
  UserPlus,
  FolderPlus,
  Users,
  Upload,
  Plus,
  Phone,
  Building,
  FileSpreadsheet,
  User,
  Trash2,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format } from "date-fns";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ROILaunchDialog from "@/components/ROILaunchDialog";
import FaxUsageWidget from "@/components/FaxUsageWidget";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  sent: { label: "Sent", color: "bg-blue-100 text-blue-800", icon: Send },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle },
  failed: { label: "Failed", color: "bg-red-100 text-red-800", icon: XCircle },
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  queued: { label: "Queued", color: "bg-slate-100 text-slate-800", icon: Clock },
  received: { label: "Received", color: "bg-emerald-100 text-emerald-800", icon: Inbox },
  unread: { label: "Unread", color: "bg-purple-100 text-purple-800", icon: Inbox },
  read: { label: "Read", color: "bg-slate-100 text-slate-600", icon: Eye },
};

export default function FaxesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("outgoing");
  const [loading, setLoading] = useState(true);
  const [outgoingFaxes, setOutgoingFaxes] = useState([]);
  const [incomingFaxes, setIncomingFaxes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFax, setSelectedFax] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Attach to client dialog
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);

  // Send fax dialog
  const [sendFaxDialogOpen, setSendFaxDialogOpen] = useState(false);
  const [sendingFax, setSendingFax] = useState(false);
  const [faxFormData, setFaxFormData] = useState({
    recipientName: "",
    recipientFax: "",
    recipientCompany: "",
    subject: "",
    notes: "",
    file: null,
    includeFacesheet: true,
    facesheetData: {
      senderName: "",
      senderPhone: "",
      senderFax: "",
      senderCompany: "",
      pageCount: "1",
      urgent: false,
      message: "",
    },
  });
  const [filePreview, setFilePreview] = useState(null);

  // ROI launch dialog
  const [roiDialogOpen, setRoiDialogOpen] = useState(false);

  // Re-fax dialog (re-send an existing outgoing fax to same/different recipient)
  const [refaxDialogOpen, setRefaxDialogOpen] = useState(false);
  const [refaxSource, setRefaxSource] = useState(null); // the fax row being re-sent
  const [refaxForm, setRefaxForm] = useState({
    recipient_fax: "",
    recipient_name: "",
    recipient_company: "",
    subject: "",
    notes: "",
    urgent: false,
  });
  const [refaxSending, setRefaxSending] = useState(false);

  // Physicians directory & fax cover templates (for prefill dropdowns)
  const [physiciansList, setPhysiciansList] = useState([]);
  const [coverTemplates, setCoverTemplates] = useState([]);

  useEffect(() => {
    // Pull the SRFax inbox on first load so brand-new inbound faxes appear
    // without requiring the user to click "Refresh" first.
    fetchFaxes({ withSync: true });
  }, []);

  // Auto-open Send Fax dialog with prefill via URL params
  // e.g. /faxes?action=send_fax&client_id=X&recipient_name=Dr+Smith&recipient_fax=6085551234&subject=MD+Order&auto_facesheet=1
  useEffect(() => {
    const action = searchParams.get("action");
    if (action !== "send_fax") return;

    const clientId = searchParams.get("client_id") || "";
    const recipientName = searchParams.get("recipient_name") || "";
    const recipientFax = searchParams.get("recipient_fax") || "";
    const recipientCompany = searchParams.get("recipient_company") || "";
    const subject = searchParams.get("subject") || "";
    const notes = searchParams.get("notes") || "";
    const autoFacesheet = searchParams.get("auto_facesheet") === "1";

    handleOpenSendFax({
      clientId,
      recipientName,
      recipientFax,
      recipientCompany,
      subject,
      notes,
      autoFacesheet,
    });

    // Clean the URL so refreshing doesn't re-trigger
    setSearchParams({}, { replace: true });
  }, []);

  const fetchFaxes = async (opts = {}) => {
    const { withSync = false } = opts;
    setLoading(true);
    try {
      // Pull latest inbound faxes from the provider (SRFax etc.) first, then list.
      if (withSync) {
        try {
          const syncRes = await axios.post(`${API}/fax/sync-inbox`);
          const imported = syncRes.data?.imported || 0;
          if (imported > 0) {
            toast.success(`${imported} new inbound fax${imported === 1 ? "" : "es"} imported`);
          }
        } catch (syncErr) {
          console.warn("Inbox sync failed (non-fatal):", syncErr);
        }
      }
      const [outgoingRes, incomingRes] = await Promise.all([
        axios.get(`${API}/fax/orders`),
        axios.get(`${API}/fax/incoming`),
      ]);
      setOutgoingFaxes(outgoingRes.data || []);
      setIncomingFaxes(incomingRes.data || []);
    } catch (error) {
      console.error("Failed to fetch faxes:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load faxes");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data || []);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchFaxes({ withSync: true });
    setRefreshing(false);
    toast.success("Inbox synced");
  };

  const handleViewFax = async (fax) => {
    setSelectedFax(fax);
    setViewDialogOpen(true);
    setPreviewUrl(null);
    setPreviewError(null);

    if (fax.direction === "incoming" && fax.status === "unread") {
      markAsRead(fax.id);
    }

    // Fetch PDF blob for inline preview (requires auth so iframe can't do it directly)
    setPreviewLoading(true);
    try {
      const endpoint = fax.direction === "incoming"
        ? `${API}/fax/incoming/${fax.id}/download`
        : `${API}/fax/orders/${fax.id}/download`;
      const res = await axios.get(endpoint, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      console.error("PDF preview failed:", err);
      setPreviewError(
        err.response?.status === 404
          ? "PDF not available for this fax yet."
          : "Couldn't load PDF preview — you can still download."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewError(null);
  };

  const markAsRead = async (faxId) => {
    try {
      await axios.patch(`${API}/fax/incoming/${faxId}/read`);
      setIncomingFaxes(prev => 
        prev.map(f => f.id === faxId ? { ...f, status: "read" } : f)
      );
    } catch (error) {
      console.error("Failed to mark fax as read:", error);
    }
  };

  const handleDownload = async (fax) => {
    try {
      const endpoint = fax.direction === "incoming" 
        ? `${API}/fax/incoming/${fax.id}/download`
        : `${API}/fax/orders/${fax.id}/download`;
        
      const response = await axios.get(endpoint, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `fax-${fax.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Fax downloaded");
    } catch (error) {
      toast.error("Failed to download fax");
    }
  };

  const handlePrint = async (fax) => {
    try {
      const endpoint = fax.direction === "incoming" 
        ? `${API}/fax/incoming/${fax.id}/download`
        : `${API}/fax/orders/${fax.id}/download`;
        
      const response = await axios.get(endpoint, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      
      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Fallback: create iframe for printing
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          iframe.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 1000);
        };
      }
    } catch (error) {
      toast.error("Failed to print fax");
    }
  };

  const handleAttachToClient = (fax) => {
    setSelectedFax(fax);
    setDocumentName(fax.subject || `Fax from ${fax.sender_name || fax.sender_fax || 'Unknown'}`);
    setSelectedClientId("");
    setAttachDialogOpen(true);
    fetchClients();
  };

  const submitAttachToClient = async () => {
    if (!selectedClientId) {
      toast.error("Please select a client");
      return;
    }
    if (!documentName.trim()) {
      toast.error("Please enter a document name");
      return;
    }

    setAttaching(true);
    try {
      await axios.post(`${API}/fax/incoming/${selectedFax.id}/attach-to-client`, {
        client_id: selectedClientId,
        document_name: documentName.trim(),
      });
      
      toast.success("Fax attached to client documents");
      setAttachDialogOpen(false);
      
      // Update fax status
      setIncomingFaxes(prev => 
        prev.map(f => f.id === selectedFax.id 
          ? { ...f, attached_to_client: selectedClientId } 
          : f
        )
      );
    } catch (error) {
      console.error("Failed to attach fax:", error);
      toast.error(error.response?.data?.detail || "Failed to attach fax to client");
    } finally {
      setAttaching(false);
    }
  };

  const handleRefreshStatus = async (faxId) => {
    try {
      await axios.get(`${API}/fax/orders/${faxId}/refresh-status`);
      toast.success("Status refreshed");
      fetchFaxes();
    } catch (error) {
      toast.error("Failed to refresh status");
    }
  };

  const [retryingId, setRetryingId] = useState(null);
  const handleRetryFax = async (faxId) => {
    setRetryingId(faxId);
    try {
      const res = await axios.post(`${API}/fax/orders/${faxId}/retry`);
      if (res.data?.success) {
        toast.success(
          `Fax resent via ${(res.data.provider || "").toUpperCase()}${
            res.data.transaction_id ? " · #" + res.data.transaction_id : ""
          }`
        );
      } else {
        toast.error(`Retry failed: ${res.data?.error_message || "Unknown error"}`);
      }
      fetchFaxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Retry request failed");
    } finally {
      setRetryingId(null);
    }
  };

  // Open the Re-fax dialog pre-filled with the source fax's recipient info
  const openRefaxDialog = (fax) => {
    if (!fax?.pdf_data && !fax?.id) {
      toast.error("This fax has no stored document to re-send.");
      return;
    }
    setRefaxSource(fax);
    setRefaxForm({
      recipient_fax: fax.recipient_fax || "",
      recipient_name: fax.recipient_name || "",
      recipient_company: fax.recipient_company || "",
      subject: fax.subject || "",
      notes: fax.notes || "",
      urgent: !!fax.urgent,
    });
    setRefaxDialogOpen(true);
  };

  const handleConfirmRefax = async () => {
    if (!refaxSource) return;
    if (!refaxForm.recipient_fax.trim()) {
      toast.error("Recipient fax number is required");
      return;
    }
    setRefaxSending(true);
    try {
      const res = await axios.post(
        `${API}/fax/orders/${refaxSource.id}/refax`,
        {
          recipient_fax: refaxForm.recipient_fax.trim(),
          recipient_name: refaxForm.recipient_name.trim() || null,
          recipient_company: refaxForm.recipient_company.trim() || null,
          subject: refaxForm.subject.trim() || null,
          notes: refaxForm.notes.trim() || null,
          urgent: refaxForm.urgent,
        }
      );
      if (res.data?.success) {
        toast.success(
          `Re-fax sent via ${(res.data.provider || "").toUpperCase() || "provider"}`
        );
        setRefaxDialogOpen(false);
        setRefaxSource(null);
      } else {
        toast.error(res.data?.error || res.data?.message || "Re-fax could not be sent");
      }
      fetchFaxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Re-fax request failed");
    } finally {
      setRefaxSending(false);
    }
  };



  // Delete fax (outgoing or incoming)
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, direction, label }
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const endpoint =
        deleteTarget.direction === "incoming"
          ? `${API}/fax/incoming/${deleteTarget.id}`
          : `${API}/fax/orders/${deleteTarget.id}`;
      await axios.delete(endpoint);
      toast.success(
        `${deleteTarget.direction === "incoming" ? "Incoming" : "Outgoing"} fax deleted`
      );
      if (deleteTarget.direction === "incoming") {
        setIncomingFaxes((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      } else {
        setOutgoingFaxes((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      }
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete fax");
    } finally {
      setDeleting(false);
    }
  };

  // Send Fax Functions
  const handleOpenSendFax = async (prefill = {}) => {
    // Fetch agency contact info to auto-populate the sender block,
    // physicians directory for lookup, and cover-message templates.
    let sender = { name: "", phone: "", fax: "", company: "" };
    let physicians = [];
    let templates = [];
    try {
      const [contactRes, physRes, tplRes] = await Promise.all([
        axios.get(`${API}/auth/me/agency-contact`),
        axios.get(`${API}/physicians`).catch(() => ({ data: [] })),
        axios.get(`${API}/fax-cover-templates`).catch(() => ({ data: [] })),
      ]);
      const info = contactRes.data || {};
      sender = {
        name: info.user_name || "",
        phone: info.user_phone || info.phone || "",
        fax: info.fax || "",
        company: info.name || "",
      };
      physicians = physRes.data || [];
      templates = tplRes.data || [];
    } catch (error) {
      console.warn("Could not load agency contact info for sender autofill", error);
    }
    setPhysiciansList(physicians);
    setCoverTemplates(templates);

    // Use default template's message if we have one and caller didn't supply one
    const defaultTpl = (templates || []).find((t) => t.is_default);
    const coverMessage =
      prefill.notes ||
      (defaultTpl ? defaultTpl.message : "");

    setFaxFormData({
      recipientName: prefill.recipientName || "",
      recipientFax: prefill.recipientFax || "",
      recipientCompany: prefill.recipientCompany || "",
      subject: prefill.subject || "",
      notes: prefill.notes || "",
      file: null,
      includeFacesheet: true,
      facesheetData: {
        senderName: sender.name,
        senderPhone: sender.phone,
        senderFax: sender.fax,
        senderCompany: sender.company,
        pageCount: "1",
        urgent: false,
        message: coverMessage,
      },
    });
    setFilePreview(null);
    setSendFaxDialogOpen(true);

    // If a client_id + auto_facesheet flag was passed, fetch the face sheet
    // PDF and attach it automatically so the user can send in one click.
    if (prefill.clientId && prefill.autoFacesheet) {
      try {
        const response = await axios.get(
          `${API}/clients/${prefill.clientId}/face-sheet.pdf`,
          { responseType: "blob" }
        );
        const blob = new Blob([response.data], { type: "application/pdf" });
        const file = new File([blob], `FaceSheet_${prefill.clientId.slice(0, 8)}.pdf`, {
          type: "application/pdf",
        });
        setFaxFormData((prev) => ({ ...prev, file }));
        setFilePreview(file.name);
        toast.success("Client face sheet attached — review and click Send");
      } catch (error) {
        console.error("Failed to load client face sheet:", error);
        toast.error("Couldn't attach face sheet. Please upload a document manually.");
      }
    }
  };

  // Pick a physician from the directory → fill recipient fields
  const handlePickPhysicianFromDirectory = (physicianId) => {
    const phys = physiciansList.find((p) => p.id === physicianId);
    if (!phys) return;
    setFaxFormData((prev) => ({
      ...prev,
      recipientName: phys.name || prev.recipientName,
      recipientFax: phys.fax || prev.recipientFax,
      recipientCompany: phys.specialty || prev.recipientCompany,
    }));
    if (!phys.fax) {
      toast.warning(`${phys.name} has no fax on file — please enter one manually`);
    } else {
      toast.success(`Loaded ${phys.name}'s fax details`);
    }
  };

  // Apply a saved cover-message template
  const handleApplyCoverTemplate = (templateId) => {
    const tpl = coverTemplates.find((t) => t.id === templateId);
    if (!tpl) return;
    setFaxFormData((prev) => ({
      ...prev,
      facesheetData: { ...prev.facesheetData, message: tpl.message },
    }));
    toast.success(`Applied template: ${tpl.name}`);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/tiff'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a PDF or image file (PNG, JPG, TIFF)");
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      
      setFaxFormData(prev => ({ ...prev, file }));
      setFilePreview(file.name);
    }
  };

  // Attachment presets — one-click attach the client's face sheet, latest
  // CMS-485, or latest MD Order to the outbound fax. Requires the dialog
  // was opened with a client context (faxFormData.clientId is set).
  const [attachingPreset, setAttachingPreset] = useState(null);
  const attachPresetDocument = async (preset) => {
    const clientId = faxFormData.clientId;
    if (!clientId) {
      toast.error("Pick a client first — presets need a client to look up.");
      return;
    }
    setAttachingPreset(preset);
    try {
      let blob, fileName;
      if (preset === "face_sheet") {
        const res = await axios.get(`${API}/clients/${clientId}/face-sheet.pdf`, { responseType: "blob" });
        blob = new Blob([res.data], { type: "application/pdf" });
        fileName = `FaceSheet_${clientId.slice(0, 8)}.pdf`;
      } else if (preset === "latest_cms485") {
        const listRes = await axios.get(`${API}/cms485`, { params: { client_id: clientId } });
        const latest = (listRes.data || [])[0];
        if (!latest) {
          toast.error("No CMS-485 on file for this client.");
          return;
        }
        const pdfRes = await axios.get(`${API}/cms485/${latest.id}/pdf`, { responseType: "blob" });
        blob = new Blob([pdfRes.data], { type: "application/pdf" });
        fileName = `CMS485_${clientId.slice(0, 8)}.pdf`;
      } else if (preset === "latest_md_order") {
        const listRes = await axios.get(`${API}/md-orders`, { params: { client_id: clientId } });
        const latest = (listRes.data || [])[0];
        if (!latest) {
          toast.error("No MD Order on file for this client.");
          return;
        }
        const pdfRes = await axios.get(`${API}/md-orders/${latest.id}/pdf`, { responseType: "blob" });
        blob = new Blob([pdfRes.data], { type: "application/pdf" });
        fileName = `MDOrder_${clientId.slice(0, 8)}.pdf`;
      } else {
        return;
      }
      const file = new File([blob], fileName, { type: "application/pdf" });
      setFaxFormData((prev) => ({ ...prev, file }));
      setFilePreview(fileName);
      toast.success(`Attached ${fileName}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't attach that document.");
    } finally {
      setAttachingPreset(null);
    }
  };

  const handleSendFax = async () => {
    // Validation
    if (!faxFormData.recipientFax.trim()) {
      toast.error("Please enter a recipient fax number");
      return;
    }
    if (!faxFormData.file) {
      toast.error("Please upload a document to fax");
      return;
    }

    setSendingFax(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(faxFormData.file);
      
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1];
        
        try {
          const response = await axios.post(`${API}/fax/send-document`, {
            recipient_name: faxFormData.recipientName.trim(),
            recipient_fax: faxFormData.recipientFax.trim(),
            recipient_company: faxFormData.recipientCompany.trim(),
            subject: faxFormData.subject.trim(),
            notes: faxFormData.notes.trim(),
            file_name: faxFormData.file.name,
            file_type: faxFormData.file.type,
            file_data: base64Data,
            include_facesheet: faxFormData.includeFacesheet,
            facesheet: faxFormData.includeFacesheet ? {
              sender_name: faxFormData.facesheetData.senderName.trim(),
              sender_phone: faxFormData.facesheetData.senderPhone.trim(),
              sender_fax: faxFormData.facesheetData.senderFax.trim(),
              sender_company: faxFormData.facesheetData.senderCompany.trim(),
              page_count: faxFormData.facesheetData.pageCount,
              urgent: faxFormData.facesheetData.urgent,
              message: faxFormData.facesheetData.message.trim(),
            } : null,
          });

          const data = response.data || {};
          if (data.success && data.status === "sent") {
            toast.success("Fax sent successfully!");
            setSendFaxDialogOpen(false);
          } else {
            // Backend saved the fax but the provider rejected/failed the send
            toast.error(data.error || data.message || "Fax could not be sent");
          }
          fetchFaxes(); // Refresh the list so the failure/success row appears
        } catch (error) {
          console.error("Failed to send fax:", error);
          toast.error(error.response?.data?.detail || "Failed to send fax");
        } finally {
          setSendingFax(false);
        }
      };
      
      reader.onerror = () => {
        toast.error("Failed to read file");
        setSendingFax(false);
      };
    } catch (error) {
      console.error("Failed to send fax:", error);
      toast.error("Failed to send fax");
      setSendingFax(false);
    }
  };

  const filteredOutgoing = outgoingFaxes.filter(fax => 
    fax.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    fax.recipient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    fax.recipient_fax?.includes(searchQuery)
  );

  const filteredIncoming = incomingFaxes.filter(fax =>
    fax.sender_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    fax.sender_fax?.includes(searchQuery) ||
    fax.subject?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unreadCount = incomingFaxes.filter(f => f.status === "unread").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Printer className="w-7 h-7 text-primary" />
              Faxes
            </h1>
            <p className="text-slate-600">Manage incoming and outgoing faxes</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Link to="/settings?tab=integrations">
            <Button variant="outline" className="gap-2">
              <Settings className="w-4 h-4" />
              Configure eFax
            </Button>
          </Link>
          <Button onClick={() => handleOpenSendFax()} className="gap-2" data-testid="send-fax-btn">
            <Plus className="w-4 h-4" />
            Send Fax
          </Button>
          <Button
            variant="outline"
            onClick={() => setRoiDialogOpen(true)}
            className="gap-2"
            data-testid="send-roi-btn"
          >
            <FileText className="w-4 h-4" />
            Send ROI
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search faxes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Fax Usage Widget */}
      <FaxUsageWidget />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="outgoing" className="gap-2" data-testid="outgoing-tab">
            <ArrowUpRight className="w-4 h-4" />
            Outgoing
            <Badge variant="secondary" className="ml-1">{outgoingFaxes.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="incoming" className="gap-2" data-testid="incoming-tab">
            <ArrowDownLeft className="w-4 h-4" />
            Incoming
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1">{unreadCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Outgoing Faxes Tab */}
        <TabsContent value="outgoing" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" />
                Outgoing Faxes
              </CardTitle>
              <CardDescription>
                Faxes sent from your agency to physicians and other recipients
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredOutgoing.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Send className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No outgoing faxes found</p>
                  <p className="text-sm">Faxes sent from client records will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOutgoing.map((fax) => {
                      const status = statusConfig[fax.status] || statusConfig.pending;
                      const StatusIcon = status.icon;
                      return (
                        <TableRow key={fax.id}>
                          <TableCell className="text-sm">
                            {fax.created_at ? format(new Date(fax.created_at), "MMM d, yyyy h:mm a") : "-"}
                          </TableCell>
                          <TableCell className="font-medium">{fax.client_name}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{fax.recipient_name}</p>
                              <p className="text-sm text-slate-500">{fax.recipient_fax}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {fax.document_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={status.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewFax({ ...fax, direction: "outgoing" })}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownload({ ...fax, direction: "outgoing" })}>
                                  <Download className="w-4 h-4 mr-2" />
                                  Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handlePrint({ ...fax, direction: "outgoing" })}>
                                  <Printer className="w-4 h-4 mr-2" />
                                  Print
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleRefreshStatus(fax.id)}>
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Refresh Status
                                </DropdownMenuItem>
                                {(fax.status === "failed" || fax.status === "queued") && (
                                  <DropdownMenuItem
                                    onClick={() => handleRetryFax(fax.id)}
                                    disabled={retryingId === fax.id}
                                    className="text-teal-700 focus:text-teal-800 focus:bg-teal-50"
                                    data-testid={`retry-outgoing-${fax.id}`}
                                  >
                                    {retryingId === fax.id ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Send className="w-4 h-4 mr-2" />
                                    )}
                                    Retry Send
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => openRefaxDialog(fax)}
                                  className="text-slate-700 focus:text-slate-900 focus:bg-slate-100"
                                  data-testid={`refax-outgoing-${fax.id}`}
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  Re-fax
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                  onClick={() =>
                                    setDeleteTarget({
                                      id: fax.id,
                                      direction: "outgoing",
                                      label:
                                        fax.subject ||
                                        `Outgoing fax to ${fax.recipient_name || fax.recipient_fax || "recipient"}`,
                                    })
                                  }
                                  data-testid={`delete-outgoing-${fax.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Fax
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incoming Faxes Tab */}
        <TabsContent value="incoming" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Inbox className="w-5 h-5 text-primary" />
                Incoming Faxes
              </CardTitle>
              <CardDescription>
                Faxes received at your agency&apos;s fax number
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredIncoming.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Inbox className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No incoming faxes found</p>
                  <p className="text-sm mt-2">
                    Configure your inbound fax number or turn on eFax inbox sync in{" "}
                    <Link to="/settings?tab=integrations" className="text-primary hover:underline">
                      Settings → Integrations
                    </Link>
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>Subject/Pages</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredIncoming.map((fax) => {
                      const status = statusConfig[fax.status] || statusConfig.received;
                      const StatusIcon = status.icon;
                      return (
                        <TableRow 
                          key={fax.id}
                          className={fax.status === "unread" ? "bg-blue-50/50" : ""}
                        >
                          <TableCell className="text-sm">
                            {fax.received_at ? format(new Date(fax.received_at), "MMM d, yyyy h:mm a") : "-"}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className={`font-medium ${fax.status === "unread" ? "text-blue-900" : ""}`}>
                                {fax.sender_name || "Unknown"}
                              </p>
                              <p className="text-sm text-slate-500">{fax.sender_fax}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className={fax.status === "unread" ? "font-semibold" : ""}>
                                {fax.subject || "No subject"}
                              </p>
                              <p className="text-sm text-slate-500">{fax.page_count || 1} page(s)</p>
                              {fax.attached_to_client && (
                                <Badge variant="outline" className="mt-1 text-xs">
                                  <FolderPlus className="w-3 h-3 mr-1" />
                                  Attached to client
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={status.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewFax({ ...fax, direction: "incoming" })}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownload({ ...fax, direction: "incoming" })}>
                                  <Download className="w-4 h-4 mr-2" />
                                  Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handlePrint({ ...fax, direction: "incoming" })}>
                                  <Printer className="w-4 h-4 mr-2" />
                                  Print
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleAttachToClient({ ...fax, direction: "incoming" })}>
                                  <FolderPlus className="w-4 h-4 mr-2" />
                                  Attach to Client
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                  onClick={() =>
                                    setDeleteTarget({
                                      id: fax.id,
                                      direction: "incoming",
                                      label:
                                        fax.subject ||
                                        `Incoming fax from ${fax.sender_name || fax.sender_fax || "sender"}`,
                                    })
                                  }
                                  data-testid={`delete-incoming-${fax.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Fax
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Fax Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={(open) => (open ? setViewDialogOpen(true) : closeViewDialog())}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              {selectedFax?.direction === "incoming" ? (
                <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
              ) : (
                <ArrowUpRight className="w-5 h-5 text-blue-600" />
              )}
              {selectedFax?.direction === "incoming" ? "Incoming" : "Outgoing"} Fax Details
            </DialogTitle>
          </DialogHeader>

          {selectedFax && (
            <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_320px] flex-1 min-h-0">
              {/* PDF Preview */}
              <div className="bg-slate-100 min-h-[65vh] flex items-center justify-center border-r" data-testid="fax-preview-pane">
                {previewLoading ? (
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading preview…
                  </div>
                ) : previewError ? (
                  <div className="text-sm text-slate-500 text-center px-6">
                    <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    {previewError}
                  </div>
                ) : previewUrl ? (
                  <iframe
                    src={previewUrl}
                    title="Fax preview"
                    className="w-full h-full min-h-[65vh]"
                    data-testid="fax-preview-iframe"
                  />
                ) : (
                  <div className="text-sm text-slate-400">No preview available</div>
                )}
              </div>

              {/* Metadata sidebar */}
              <div className="p-5 space-y-4 overflow-y-auto">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">
                    {selectedFax.direction === "incoming" ? "From" : "To"}
                  </p>
                  <p className="font-medium text-slate-900">
                    {selectedFax.direction === "incoming"
                      ? (selectedFax.sender_name || "Unknown")
                      : selectedFax.recipient_name}
                  </p>
                  <p className="text-sm text-slate-600">
                    {selectedFax.direction === "incoming"
                      ? selectedFax.sender_fax
                      : selectedFax.recipient_fax}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Date</p>
                  <p className="font-medium text-slate-900">
                    {format(
                      new Date(selectedFax.direction === "incoming"
                        ? selectedFax.received_at
                        : selectedFax.created_at),
                      "MMMM d, yyyy h:mm a"
                    )}
                  </p>
                </div>
                {selectedFax.direction === "outgoing" && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Client</p>
                    <p className="font-medium text-slate-900">{selectedFax.client_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Status</p>
                  <Badge className={statusConfig[selectedFax.status]?.color}>
                    {statusConfig[selectedFax.status]?.label || selectedFax.status}
                  </Badge>
                </div>
                {selectedFax.direction === "outgoing" && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Document Type</p>
                    <p className="font-medium text-slate-900">
                      {selectedFax.document_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                )}
                {selectedFax.page_count && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Pages</p>
                    <p className="font-medium text-slate-900">{selectedFax.page_count}</p>
                  </div>
                )}

                {selectedFax.notes && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Notes</p>
                    <p className="text-sm bg-slate-50 p-3 rounded-lg mt-1">{selectedFax.notes}</p>
                  </div>
                )}

                {selectedFax.error_message && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {selectedFax.error_message}
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-2 pt-4 border-t">
                  {previewUrl && (
                    <Button
                      variant="outline"
                      onClick={() => window.open(previewUrl, "_blank")}
                      className="gap-2 w-full"
                      data-testid="open-fax-newtab-btn"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open in new tab
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => handlePrint(selectedFax)} className="gap-2 w-full">
                    <Printer className="w-4 h-4" />
                    Print
                  </Button>
                  <Button variant="outline" onClick={() => handleDownload(selectedFax)} className="gap-2 w-full">
                    <Download className="w-4 h-4" />
                    Download PDF
                  </Button>
                  {selectedFax.direction === "incoming" && (
                    <Button onClick={() => {
                      closeViewDialog();
                      handleAttachToClient(selectedFax);
                    }} className="gap-2 w-full bg-teal-600 hover:bg-teal-700">
                      <FolderPlus className="w-4 h-4" />
                      Attach to Client
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attach to Client Dialog */}
      <Dialog open={attachDialogOpen} onOpenChange={setAttachDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="w-5 h-5 text-primary" />
              Attach Fax to Client
            </DialogTitle>
            <DialogDescription>
              Save this fax to a client&apos;s documents for easy reference.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="client-select">Select Client</Label>
              {loadingClients ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading clients...
                </div>
              ) : (
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger id="client-select" data-testid="client-select">
                    <SelectValue placeholder="Select a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          {client.first_name} {client.last_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="document-name">Document Name</Label>
              <Input
                id="document-name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="Enter document name..."
                data-testid="document-name-input"
              />
              <p className="text-xs text-slate-500">
                This name will appear in the client&apos;s documents list
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitAttachToClient} 
              disabled={attaching || !selectedClientId}
              data-testid="attach-fax-btn"
            >
              {attaching ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Attaching...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <FolderPlus className="w-4 h-4" />
                  Attach to Client
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Fax Dialog */}
      <Dialog open={sendFaxDialogOpen} onOpenChange={setSendFaxDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Send Fax
            </DialogTitle>
            <DialogDescription>
              Upload a document and enter recipient details to send a fax.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Physicians Directory quick-pick */}
            {physiciansList.length > 0 && (
              <div className="space-y-1 rounded-md border border-teal-100 bg-teal-50/50 p-3">
                <Label htmlFor="physician-picker" className="text-xs font-medium text-teal-900">
                  Look up from Physicians Directory
                </Label>
                <Select onValueChange={handlePickPhysicianFromDirectory}>
                  <SelectTrigger
                    id="physician-picker"
                    className="h-9 bg-white"
                    data-testid="physician-directory-picker"
                  >
                    <SelectValue placeholder="Select a physician to auto-fill recipient…" />
                  </SelectTrigger>
                  <SelectContent>
                    {physiciansList.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{p.name}</span>
                          {p.fax ? (
                            <span className="text-xs text-slate-500">· Fax: {p.fax}</span>
                          ) : (
                            <span className="text-xs text-amber-600">· No fax on file</span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Recipient Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="recipient-name">Recipient Name</Label>
                <Input
                  id="recipient-name"
                  value={faxFormData.recipientName}
                  onChange={(e) => setFaxFormData(prev => ({ ...prev, recipientName: e.target.value }))}
                  placeholder="Dr. John Smith"
                  data-testid="recipient-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipient-fax">
                  Fax Number <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="recipient-fax"
                    value={faxFormData.recipientFax}
                    onChange={(e) => setFaxFormData(prev => ({ ...prev, recipientFax: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                    className="pl-10"
                    data-testid="recipient-fax-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recipient-company">Company/Organization</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="recipient-company"
                  value={faxFormData.recipientCompany}
                  onChange={(e) => setFaxFormData(prev => ({ ...prev, recipientCompany: e.target.value }))}
                  placeholder="ABC Medical Center"
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="fax-subject">Subject</Label>
              <Input
                id="fax-subject"
                value={faxFormData.subject}
                onChange={(e) => setFaxFormData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Patient Records Request"
              />
            </div>
            
            {/* File Upload */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>
                  Document <span className="text-red-500">*</span>
                </Label>
                {faxFormData.clientId && (
                  <Select
                    value=""
                    onValueChange={(v) => v && attachPresetDocument(v)}
                    disabled={!!attachingPreset}
                  >
                    <SelectTrigger
                      className="h-8 w-52 text-xs"
                      data-testid="attachment-preset-select"
                    >
                      <SelectValue placeholder={attachingPreset ? "Attaching…" : "Attach from client…"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="face_sheet">Face Sheet</SelectItem>
                      <SelectItem value="latest_md_order">Latest MD Order</SelectItem>
                      <SelectItem value="latest_cms485">Latest CMS-485</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  id="fax-file"
                  accept=".pdf,.png,.jpg,.jpeg,.tiff"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="fax-file" className="cursor-pointer">
                  {filePreview ? (
                    <div className="flex items-center justify-center gap-2 text-primary">
                      <FileText className="w-8 h-8" />
                      <div className="text-left">
                        <p className="font-medium">{filePreview}</p>
                        <p className="text-sm text-slate-500">Click to change file</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <Upload className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                      <p className="font-medium">Click to upload document</p>
                      <p className="text-sm">PDF, PNG, JPG, or TIFF (max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Facesheet / Cover Page Option */}
            <div className="border rounded-lg p-4 space-y-4 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  <div>
                    <Label htmlFor="include-facesheet" className="font-medium cursor-pointer">
                      Include Facesheet (Cover Page)
                    </Label>
                    <p className="text-xs text-slate-500">Add a professional cover page with sender/recipient details</p>
                  </div>
                </div>
                <Switch
                  id="include-facesheet"
                  checked={faxFormData.includeFacesheet}
                  onCheckedChange={(checked) => setFaxFormData(prev => ({ ...prev, includeFacesheet: checked }))}
                />
              </div>
              
              {faxFormData.includeFacesheet && (
                <div className="space-y-4 pt-2 border-t">
                  <p className="text-sm font-medium text-slate-700">Sender Information</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="sender-name" className="text-xs">Your Name</Label>
                      <div className="relative">
                        <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          id="sender-name"
                          value={faxFormData.facesheetData.senderName}
                          onChange={(e) => setFaxFormData(prev => ({
                            ...prev,
                            facesheetData: { ...prev.facesheetData, senderName: e.target.value }
                          }))}
                          placeholder="Your Name"
                          className="pl-8 h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="sender-company" className="text-xs">Company/Agency</Label>
                      <div className="relative">
                        <Building className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          id="sender-company"
                          value={faxFormData.facesheetData.senderCompany}
                          onChange={(e) => setFaxFormData(prev => ({
                            ...prev,
                            facesheetData: { ...prev.facesheetData, senderCompany: e.target.value }
                          }))}
                          placeholder="Your Agency Name"
                          className="pl-8 h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="sender-phone" className="text-xs">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          id="sender-phone"
                          value={faxFormData.facesheetData.senderPhone}
                          onChange={(e) => setFaxFormData(prev => ({
                            ...prev,
                            facesheetData: { ...prev.facesheetData, senderPhone: e.target.value }
                          }))}
                          placeholder="(555) 123-4567"
                          className="pl-8 h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="sender-fax" className="text-xs">Your Fax Number</Label>
                      <div className="relative">
                        <Printer className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          id="sender-fax"
                          value={faxFormData.facesheetData.senderFax}
                          onChange={(e) => setFaxFormData(prev => ({
                            ...prev,
                            facesheetData: { ...prev.facesheetData, senderFax: e.target.value }
                          }))}
                          placeholder="(555) 123-4568"
                          className="pl-8 h-9 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="page-count" className="text-xs">Total Pages (including cover)</Label>
                      <Input
                        id="page-count"
                        type="number"
                        min="1"
                        value={faxFormData.facesheetData.pageCount}
                        onChange={(e) => setFaxFormData(prev => ({
                          ...prev,
                          facesheetData: { ...prev.facesheetData, pageCount: e.target.value }
                        }))}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="space-y-1 flex items-end pb-1">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="urgent-fax"
                          checked={faxFormData.facesheetData.urgent}
                          onCheckedChange={(checked) => setFaxFormData(prev => ({
                            ...prev,
                            facesheetData: { ...prev.facesheetData, urgent: checked }
                          }))}
                        />
                        <Label htmlFor="urgent-fax" className="text-sm cursor-pointer flex items-center gap-1">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          Mark as URGENT
                        </Label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="facesheet-message" className="text-xs">Cover Page Message</Label>
                      {coverTemplates.length > 0 && (
                        <Select onValueChange={handleApplyCoverTemplate}>
                          <SelectTrigger
                            className="h-7 w-[220px] text-xs"
                            data-testid="cover-template-picker"
                          >
                            <SelectValue placeholder="Load a saved template…" />
                          </SelectTrigger>
                          <SelectContent>
                            {coverTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.is_default ? "★ " : ""}{t.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <Textarea
                      id="facesheet-message"
                      value={faxFormData.facesheetData.message}
                      onChange={(e) => setFaxFormData(prev => ({
                        ...prev,
                        facesheetData: { ...prev.facesheetData, message: e.target.value }
                      }))}
                      placeholder="Please review the attached documents regarding patient care..."
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="fax-notes">Additional Notes (Optional)</Label>
              <Textarea
                id="fax-notes"
                value={faxFormData.notes}
                onChange={(e) => setFaxFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Internal notes (not included in fax)..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendFaxDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendFax} 
              disabled={sendingFax || !faxFormData.recipientFax || !faxFormData.file}
              data-testid="submit-send-fax-btn"
            >
              {sendingFax ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Send Fax
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ROI Launch Dialog */}
      <ROILaunchDialog
        open={roiDialogOpen}
        onOpenChange={setRoiDialogOpen}
      />

      {/* Re-fax dialog */}
      <Dialog open={refaxDialogOpen} onOpenChange={(o) => { if (!o) { setRefaxDialogOpen(false); setRefaxSource(null); } }}>
        <DialogContent className="max-w-lg" data-testid="refax-dialog">
          <DialogHeader>
            <DialogTitle>Re-fax</DialogTitle>
            <DialogDescription>
              Re-send this fax (same document) to the same recipient or a
              corrected number. A new entry is created in your outbox — the
              original stays untouched.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {refaxSource?.subject && (
              <div className="text-sm text-slate-600 bg-slate-50 rounded-md px-3 py-2">
                <span className="font-medium">Document:</span> {refaxSource.subject}
                {refaxSource.file_name ? ` (${refaxSource.file_name})` : ""}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="refax-fax">Recipient Fax Number *</Label>
              <Input
                id="refax-fax"
                data-testid="refax-fax-input"
                value={refaxForm.recipient_fax}
                onChange={(e) => setRefaxForm((p) => ({ ...p, recipient_fax: e.target.value }))}
                placeholder="+1 (555) 555-0100"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="refax-name">Recipient Name</Label>
                <Input
                  id="refax-name"
                  value={refaxForm.recipient_name}
                  onChange={(e) => setRefaxForm((p) => ({ ...p, recipient_name: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="refax-company">Company</Label>
                <Input
                  id="refax-company"
                  value={refaxForm.recipient_company}
                  onChange={(e) => setRefaxForm((p) => ({ ...p, recipient_company: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="refax-subject">Subject</Label>
              <Input
                id="refax-subject"
                value={refaxForm.subject}
                onChange={(e) => setRefaxForm((p) => ({ ...p, subject: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="refax-notes">Notes</Label>
              <Textarea
                id="refax-notes"
                rows={2}
                value={refaxForm.notes}
                onChange={(e) => setRefaxForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
            <label
              className="flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors border-slate-200 hover:bg-slate-50 has-[:checked]:border-red-300 has-[:checked]:bg-red-50"
              data-testid="refax-urgent-toggle"
            >
              <input
                type="checkbox"
                className="mt-0.5 accent-red-600"
                checked={refaxForm.urgent}
                onChange={(e) => setRefaxForm((p) => ({ ...p, urgent: e.target.checked }))}
              />
              <span className="text-sm">
                <span className="font-medium text-slate-900">Mark as URGENT</span>
                <span className="block text-xs text-slate-600">Adds URGENT stamp + banner to the cover.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRefaxDialogOpen(false); setRefaxSource(null); }} disabled={refaxSending}>
              Cancel
            </Button>
            <Button onClick={handleConfirmRefax} disabled={refaxSending} data-testid="refax-confirm-btn">
              {refaxSending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>) : (<><Send className="w-4 h-4 mr-2" />Send Re-fax</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-fax-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this fax?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.label
                ? `"${deleteTarget.label}" will be permanently removed from your records.`
                : "This fax will be permanently removed from your records."}{" "}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              onClick={handleConfirmDelete}
              data-testid="delete-fax-confirm-btn"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
